import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Plane, ArrowRight, Clock } from "lucide-react";
import { CITIES, POPULAR } from "../data/cities";
import { computeFare } from "../data/pricing";
import { useLang } from "../i18n/LanguageContext";
import { useSeo } from "../lib/seo";
import Reveal from "../components/Reveal";
import SEO_CITIES from "../data/seoCities.json";

// Real road distances (Google Distance Matrix) for major cities → real fixed prices.
const EXACT_KM = {};
const ALIAS = { genf: "genève", genève: "genève" };
SEO_CITIES.forEach((c) => { if (c.exact) EXACT_KM[c.n.toLowerCase()] = c.km; });
const kmOf = (c) => EXACT_KM[c.name.toLowerCase()] ?? EXACT_KM[ALIAS[c.name.toLowerCase()] || "_"] ?? c.km;

const RT = {
  de: { kicker: "Routen", title: "Beliebte Flughafentransfer-Routen ab Zürich.", sub: "Fixpreis-Taxi & Privattransfers auf den meistgebuchten Routen ab Flughafen Zürich (ZRH) — Distanz, Fahrzeit und Preis auf einen Blick." },
  en: { kicker: "Routes", title: "Popular airport transfer routes from Zürich.", sub: "Fixed-fare taxi & private transfers on the most-booked routes from Zürich Airport (ZRH) — see distance, travel time and price at a glance." },
  fr: { kicker: "Itinéraires", title: "Itinéraires de transfert populaires depuis Zurich.", sub: "Taxi à prix fixe & transferts privés sur les itinéraires les plus réservés depuis l'aéroport de Zurich (ZRH) — distance, durée et prix en un coup d'œil." },
  it: { kicker: "Tratte", title: "Tratte di transfer popolari da Zurigo.", sub: "Taxi a tariffa fissa e transfer privati sulle tratte più prenotate dall'aeroporto di Zurigo (ZRH) — distanza, tempo e prezzo a colpo d'occhio." },
};

// Popular list first, then the rest of the cities appended.
const ROUTES = [...POPULAR, ...CITIES.filter((c) => !POPULAR.some((p) => p.slug === c.slug))];

const Routen = () => {
  const { lang } = useLang();
  const r = RT[lang] || RT.de;
  const navigate = useNavigate();
  useSeo({ title: `${r.title} | Airport Taxi Transfers`, description: r.sub, path: "/ziele" });
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div className="pt-[68px] min-h-screen bg-white overflow-x-hidden" data-testid="routes-page">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-14 lg:py-20">
        <Reveal>
          <p className="font-body text-sm text-[#2B4BFF] font-semibold mb-3">{r.kicker}</p>
          <h1 className="font-heading font-bold tracking-tight text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-[#0B1533]">{r.title}</h1>
          <p className="font-body text-base sm:text-lg text-[#5A607A] mt-5 max-w-2xl leading-relaxed">{r.sub}</p>
        </Reveal>

        <div className="mt-10 space-y-3" data-testid="routes-list">
          {ROUTES.map((c, i) => (
            <Reveal key={c.slug} delay={Math.min(i, 8) * 40}>
              <button onClick={() => navigate(c.path)} className="group w-full flex items-center gap-3 rounded-2xl border border-[#E9E5DE] bg-white px-4 sm:px-5 py-4 hover:border-[#2B4BFF] hover:elev-soft transition-all text-left" data-testid={`route-${c.slug}`}>
                <Plane size={18} className="text-[#2B4BFF] shrink-0" />
                <span className="flex items-baseline gap-2 min-w-0 flex-1">
                  <span className="font-heading font-semibold text-[#0B1533]">Zürich (ZRH)</span>
                  <ArrowRight size={15} className="text-[#9CA0B0] shrink-0" />
                  <span className="font-heading font-semibold text-[#0B1533] truncate">{c.name}</span>
                </span>
                <span className="hidden sm:flex items-center gap-1 font-body text-xs text-[#5A607A] shrink-0"><Clock size={12} /> ~{c.min} min</span>
                <span className="font-heading font-bold text-[#0B1533] shrink-0 ml-1">CHF {computeFare("economy", kmOf(c))}</span>
                <ArrowRight size={16} className="text-[#2B4BFF] group-hover:translate-x-1 transition-transform shrink-0" />
              </button>
            </Reveal>
          ))}
        </div>

        <div className="mt-10 rounded-2xl bg-[#0B1533] p-6 sm:p-8 flex flex-col sm:flex-row items-center justify-between gap-4" data-testid="all-destinations-cta">
          <div>
            <p className="font-heading font-bold text-white text-lg">{{ de: "Alle 2128 Gemeinden der Schweiz", en: "All 2128 Swiss municipalities", fr: "Les 2128 communes de Suisse", it: "Tutti i 2128 comuni svizzeri" }[lang] || "Alle 2128 Gemeinden der Schweiz"}</p>
            <p className="font-body text-sm text-white/70 mt-1">{{ de: "Nach Kanton geordnet – finden Sie Ihre Gemeinde.", en: "Sorted by canton – find your town.", fr: "Classées par canton – trouvez votre commune.", it: "Ordinate per cantone – trova il tuo comune." }[lang] || ""}</p>
          </div>
          <button onClick={() => navigate("/ziele-alle")} data-testid="view-all-destinations-btn" className="inline-flex items-center gap-2 bg-[#2B4BFF] text-white font-body font-semibold rounded-full px-6 py-3 hover:bg-[#1E38D8] transition-colors shrink-0">
            {{ de: "Alle Destinationen", en: "All destinations", fr: "Toutes les destinations", it: "Tutte le destinazioni" }[lang] || "Alle Destinationen"} <ArrowRight size={16} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default Routen;
